import { Extensions } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';
import { CommentsKit } from '@tiptap-pro/extension-comments';
import { Color, TextStyle } from '@tiptap/extension-text-style';
import { getHierarchicalIndexes, TableOfContents, TableOfContentData } from '@tiptap/extension-table-of-contents';

import { Attachment } from 'containers/Tiptap/components/tiptap-node/attachment-node';
import { ImageUploadNode } from 'containers/Tiptap/components/tiptap-node/image-upload-node';
import { NodeBackground } from 'containers/Tiptap/components/tiptap-extension/node-background-extension';
import { NodeAlignment } from 'containers/Tiptap/components/tiptap-extension/node-alignment-extension';
import { TocNode } from 'containers/Tiptap/components/tiptap-node/toc-node/extensions/toc-node-extension';

import { Pages } from '@tiptap-pro/extension-pages';
import { PageBreak } from '@tiptap-pro/extension-pagebreak';
import {
  DocumentViewMode,
  readDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';

import { createCoreExtensions } from './core';
import { readOnlyPagesOptions } from 'containers/Tiptap/lib/pages-options';

// Read-only schema for rendering a published/shared/snapshot document (shared link, version
// history preview, acknowledgement view). It MUST stay a superset of every mark/node the
// notion-like authoring editor can produce — otherwise setContent throws on the first unknown
// mark/node (e.g. textStyle) and blanks the whole page. Keep this as the single source of truth
// so the read schema can't drift from the write schema again (ENG-11219, ENG-11502).
export function createPreviewExtensions(
  onTocUpdate: (content: TableOfContentData) => void,
  ydoc: YDoc | null = null
): Extensions {
  return [
    ...createCoreExtensions({ ydoc }),
    Attachment,
    ImageUploadNode,
    CommentsKit.configure({ provider: null }),
    NodeBackground.configure({
      types: [
        'paragraph',
        'heading',
        'blockquote',
        'taskList',
        'bulletList',
        'orderedList',
        'tableCell',
        'tableHeader',
        'tocNode',
      ],
    }),
    NodeAlignment,
    TextStyle,
    Color,
    TableOfContents.configure({
      getIndex: getHierarchicalIndexes,
      onUpdate: onTocUpdate,
    }),
    TocNode,
    ...(ydoc && readDocumentSettings(ydoc).viewMode === DocumentViewMode.Pages
      ? [Pages.configure(readOnlyPagesOptions()), PageBreak]
      : []),
  ];
}
